import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IndexedStrings, TableSelection } from '../../components/SelectableTable/SelectableTable.types';
import { IPageInfo } from '../../models/page.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { DataInfoType, defaultCardFilter } from '../../reducers/filtersReducer/filterSlice.types';
import { CardsSortBy, ISolutionCardsTable, ITasksSolutionSheet } from '../../models/solutionCard.types';
import FilterStateGroup from '../../models/filterStateGroup.types';
import _ from 'lodash';
import { Committee, getCommitteeId, getCommitteeLabel } from '../../models/committees.types';
import { SignalRContext } from '../../Context/SignalR.context';
import { CallbackMethodNames } from '../../models/signalR.types';
import { Notification, NotificationTypeId } from '../../models/notification.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { AxiosError } from 'axios';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { getSolutionSheetsApi } from '../../services/images/images.service';
import { convertToFormatDate, convertToFormatTime } from '../../utils/dateFunctionUtils';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';

export const useCardsTableData = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { tasksSolutionSheetId } = useParams() as {
    tasksSolutionSheetId: string;
  };
  const dataInfoType = DataInfoType.CardsInfo;
  const cardsDataInfo = useSelector((state: RootState) => state.filters[dataInfoType]);
  const { showLoader } = useLayoutContext();

  const abortController = useRef<AbortController>();

  const [filter, setFilter] = useState<IndexedStrings>(cardsDataInfo.filterData.filter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);

  const [cards, setCards] = useState<ITasksSolutionSheet[]>([]);

  const [itemsSelection, setItemsSelection] = useState<TableSelection>({ items: [] });
  const [forceDeselectAllItems, setForceDeselectAllItems] = useState<boolean>(false);

  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);
  const [examCommitteeFilter, setExamCommitteeFilter] = useState<string[]>(
    cardsDataInfo.filterData.filter.examCommitteeId,
  );
  const [tasksSolutionSheetStatusId, setTasksSolutionSheetStatusId] = useState<string[]>(
    cardsDataInfo.filterData.filter.status,
  );

  const [sortByColumnId, setSortByColumnId] = useState<CardsSortBy>(cardsDataInfo.filterData.sort.sortByColumnId);
  const [isDescending, setIsDescending] = useState<boolean>(cardsDataInfo.filterData.sort.isDescending);

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...cardsDataInfo.filterData.pageInfo,
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : cardsDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [dispatch, dataInfoType, cardsDataInfo.filterData.pageInfo],
  );

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < cardsDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const getCards = (silent?: boolean, signal?: AbortSignal) => {
    const filters = Object.keys(filter).map((key) => {
      if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });

    if (!isValidFilter) return;

    showLoader(!silent);
    getSolutionSheetsApi(
      signal ?? undefined,
      cardsDataInfo.filterData.pageInfo.pageSize,
      cardsDataInfo.filterData.pageInfo.currentPage,
      sortByColumnId,
      isDescending,
      ...(filters as string[]),
    )
      .then(
        (result: ISolutionCardsTable) => {
          setCards(result.tasksSolutionSheets);
          if (result.page) {
            handleCurrentPage(result.page);
          }
        },
        (error: AxiosError) => {
          if (error?.message !== 'canceled') {
            history.push(getErrorPageUrl(error?.code ?? 500));
          }
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const getFormattedDateRangeValue = () => {
    const dateFrom = filter.bundleImportFrom.toString();
    const dateTo = filter.bundleImportTo.toString();
    return `${convertToFormatDate(dateFrom)} ${convertToFormatTime(dateFrom)}  - ${convertToFormatDate(
      dateTo,
    )} ${convertToFormatTime(dateTo)}`;
  };

  const getFormattedDateRangeValueProcessDate = () => {
    const dateFrom = filter.processingDateFrom.toString();
    const dateTo = filter.processingDateTo.toString();
    return `${convertToFormatDate(dateFrom)} ${convertToFormatTime(dateFrom)}  - ${convertToFormatDate(
      dateTo,
    )} ${convertToFormatTime(dateTo)}`;
  };

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (Object.values(filter).some((e) => (_.isArray(e) ? e.length > 0 : e !== ''))) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.examCommitteeId.length > 0) {
      const newCheckedItems =
        typeof filter.examCommitteeId === 'string'
          ? getCommitteeLabel(parseInt(filter.examCommitteeId) as Committee)
          : filter.examCommitteeId.map((id: string) => getCommitteeLabel(parseInt(id) as Committee));

      state.push({
        name: 'Komisja:',
        checkedItems: [...newCheckedItems],
      });
    }
    if (filter.bundleImportFrom.length > 0) {
      state.push({ name: 'Data i godz. importu:', checkedItems: [getFormattedDateRangeValue()] });
    }
    if (filter.processingDateFrom.length > 0) {
      state.push({ name: 'Data i godz. przetwarzania:', checkedItems: [getFormattedDateRangeValueProcessDate()] });
    }
    if (filter.status.length > 0) {
      state.push({ name: 'Status:', checkedItems: [...filter.status] });
    }
    setFilterState(state);
  }, [filter]);

  useEffect(() => {
    if (abortController && abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();
    const { signal } = abortController.current;
    getCards(undefined, signal);
  }, [
    cardsDataInfo.filterData.pageInfo.currentPage,
    cardsDataInfo.filterData.pageInfo.pageSize,
    filter,
    isDescending,
    sortByColumnId,
  ]);

  SignalRContext.useSignalREffect(
    CallbackMethodNames.DeleteTasksSolutionSheetsMessage,
    (message: Notification) => {
      if (
        message.notificationType === NotificationTypeId.DeleteTasksSolutionSheetsCompletedWithIssues ||
        message.notificationType === NotificationTypeId.DeleteTasksSolutionSheetsCompletedWithoutIssues
      ) {
        getCards();
        setForceDeselectAllItems(true);
      }
    },
    [
      cardsDataInfo.filterData.pageInfo.currentPage,
      cardsDataInfo.filterData.pageInfo.pageSize,
      filter,
      isDescending,
      sortByColumnId,
    ],
  );

  const uncheckAllFilters = () => {
    setExamCommitteeFilter([]);
    setFilterState([]);
    setFilter(defaultCardFilter);
    dispatch(filtersActions.resetFilter(dataInfoType));
  };

  const handleUncheckExamCommitteeItem = useCallback(
    (value: string) => {
      const id = getCommitteeId(value);
      const val = examCommitteeFilter.some((f) => f === id)
        ? examCommitteeFilter.filter((f) => f !== id)
        : [...examCommitteeFilter, id];
      setExamCommitteeFilter(val);
      setFilter((state) => ({
        ...state,
        examCommitteeId: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              examCommitteeId: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [examCommitteeFilter],
  );

  const handleUnchecktasksSolutionSheetStatusIdItem = useCallback(
    (value: string) => {
      const val = tasksSolutionSheetStatusId.some((f) => f === value)
        ? tasksSolutionSheetStatusId.filter((f) => f !== value)
        : [...tasksSolutionSheetStatusId, value];
      setTasksSolutionSheetStatusId(val);
      setFilter((state) => ({
        ...state,
        status: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              status: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [tasksSolutionSheetStatusId],
  );

  const handleUncheckDateImport = () => {
    setFilter((state) => ({
      ...state,
      bundleImportFrom: '',
      bundleImportTo: '',
    }));
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          filter: {
            bundleImportFrom: '',
            bundleImportTo: '',
          },
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
  };

  const handleUncheckDateProcess = () => {
    setFilter((state) => ({
      ...state,
      processingDateFrom: '',
      processingDateTo: '',
    }));
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          filter: {
            processingDateFrom: '',
            processingDateTo: '',
          },
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
  };

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      if (abortController && abortController.current && filter[name] !== value) {
        abortController.current.abort();
      }
      if (filter[name] === value) return;

      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      setFilter((state) => {
        if (state[name] !== value) {
          if (!tasksSolutionSheetId || value === '') {
            return {
              ...state,
              [name]: value,
            };
          } else {
            return {
              ...state,
              [name]: name === 'tasksSolutionSheetId' ? tasksSolutionSheetId : value,
            };
          }
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              [name]: value,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );

      const newValue = value;
      if (value && typeof value === 'string') {
        if (name === 'examCommitteeId' && !examCommitteeFilter.some((e) => e === value)) {
          setExamCommitteeFilter((arr: string[]) => [...arr, value]);
        }
        if (name === 'status' && !tasksSolutionSheetStatusId.some((e) => e === value)) {
          setTasksSolutionSheetStatusId((arr: string[]) => [...arr, value]);
        }
      } else if (value) {
        if (name === 'examCommitteeId') {
          setExamCommitteeFilter([...newValue]);
        }
        if (name === 'status') {
          setTasksSolutionSheetStatusId([...newValue]);
        }
      }
    },
    [filter, dispatch, dataInfoType, tasksSolutionSheetId, examCommitteeFilter, tasksSolutionSheetStatusId],
  );

  useEffect(() => {
    if (tasksSolutionSheetId) {
      handleFilterChange('tasksSolutionSheetId', tasksSolutionSheetId);
    }
  }, [handleFilterChange, tasksSolutionSheetId]);

  const uncheckFilterItem = (item?: string) => {
    if (item) {
      const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name) {
        if (element.name === 'Komisja:') {
          handleUncheckExamCommitteeItem(item);
        }
        if (element.name === 'Data i godz. importu:') {
          handleUncheckDateImport();
        }
        if (element.name === 'Data i godz. przetwarzania:') {
          handleUncheckDateProcess();
        }
        if (element.name === 'Status:') {
          handleUnchecktasksSolutionSheetStatusIdItem(item);
        }
      }
    }
  };

  const getFilterValues = () => {
    return Object.keys(filter).map((key) => {
      if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });
  };

  const setIsDescendingOrder = (order: boolean) => {
    setIsDescending(order);
    dispatch(filtersActions.updateFilter({ filterType: dataInfoType, newValues: { sort: { isDescending: order } } }));
  };

  const setSortByColumn = (column: CardsSortBy) => {
    setSortByColumnId(column);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { sortByColumnId: column } },
      }),
    );
  };

  return {
    filter,
    pageInfo: cardsDataInfo.filterData.pageInfo,
    cards,
    itemsSelection,
    setItemsSelection,
    forceDeselectAllItems,
    setForceDeselectAllItems,
    filterState,
    examCommitteeFilter,
    tasksSolutionSheetStatusId,
    sortByColumnId,
    setSortByColumnId: setSortByColumn,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    handleFilterChange,
    getCards,
    uncheckAllFilters,
    uncheckFilterItem,
    getPages,
    getFilterValues,
  };
};
